/**
 * 营业执照工具类
 * 用于处理营业执照ID的编码、解码和URL生成
 */

/**
 * 生成可公开访问的营业执照URL
 * @param {string} id 营业执照ID
 * @param {boolean} absolute 是否返回绝对URL（包含域名）
 * @returns {string} 格式化后的URL
 */
export const generatePublicLicenseUrl = (id, absolute = true) => {
  // 确保ID正确编码，特别处理加号(+)等特殊字符
  const encodedId = encodeURIComponent(id);
  
  // 根据环境获取基础URL
  const baseUrl = absolute 
    ? (process.env.REACT_APP_BASE_URL || window.location.origin)
    : '';
    
  return `${baseUrl}/?id=${encodedId}`;
};

/**
 * 解析URL中的ID参数
 * @param {string} urlStr URL字符串或查询参数部分
 * @returns {string|null} 解析后的ID，如果不存在则返回null
 */
export const parseLicenseIdFromUrl = (urlStr) => {
  try {
    let searchParams;
    
    if (urlStr.startsWith('http') || urlStr.startsWith('/')) {
      // 完整URL或路径，提取查询部分
      const url = new URL(urlStr, window.location.origin);
      searchParams = url.searchParams;
    } else if (urlStr.startsWith('?')) {
      // 查询字符串部分
      searchParams = new URLSearchParams(urlStr);
    } else {
      // 假设是ID本身
      return urlStr;
    }
    
    // 获取id参数
    const id = searchParams.get('id');
    
    // 修复URL编码过程中+号被解释为空格的问题
    if (id && id.includes(' ')) {
      return id.replace(/ /g, '+');
    }
    
    return id || null;
  } catch (error) {
    console.error('解析URL参数失败:', error);
    return null;
  }
};

/**
 * 在管理员界面上，生成"查看"按钮的URL
 * @param {string} id 营业执照ID
 * @returns {string} 管理员查看URL
 */
export const generateAdminViewUrl = (id) => {
  return `/license/${encodeURIComponent(id)}`;
};

/**
 * 复制营业执照公开访问链接到剪贴板
 * @param {string} id 营业执照ID
 * @returns {Promise<boolean>} 是否复制成功
 */
export const copyPublicLicenseUrl = async (id) => {
  const url = generatePublicLicenseUrl(id, true);
  
  try {
    // 方法1：尝试使用现代clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(url);
      return true;
    }
    
    // 方法2：降级方案 - 使用传统的execCommand
    return fallbackCopyToClipboard(url);
  } catch (error) {
    console.error('复制URL失败:', error);
    
    // 方法3：最后的降级方案
    try {
      return fallbackCopyToClipboard(url);
    } catch (fallbackError) {
      console.error('降级复制方案也失败:', fallbackError);
      return false;
    }
  }
};

/**
 * 降级复制方案：使用传统的document.execCommand
 * @param {string} text 要复制的文本
 * @returns {boolean} 是否复制成功
 */
const fallbackCopyToClipboard = (text) => {
  try {
    // 创建临时的textarea元素
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // 设置样式使其不可见
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    textArea.style.opacity = '0';
    
    // 添加到DOM
    document.body.appendChild(textArea);
    
    // 选择文本
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);
    
    // 执行复制命令
    const successful = document.execCommand('copy');
    
    // 清理：移除临时元素
    document.body.removeChild(textArea);
    
    return successful;
  } catch (error) {
    console.error('降级复制方案失败:', error);
    return false;
  }
}; 

/**
 * Determines the license version based on the version number
 * @param {number|string} version - The license version number
 * @returns {number} - Returns 1 for old version (V1), 2 for new version (V2)
 */
export const determineLicenseVersion = (version) => {
  if (Number(version) === 2) {
    return 2; // New version V2
  }
  return 1; // Default to old version V1
};

/**
 * Formats the validity period based on version
 * @param {string} validityPeriod - The validity period string
 * @param {string} initialIssueDate - The initial issue date
 * @param {number} version - The license version
 * @returns {string} - Formatted validity period
 */
export const formatValidityPeriod = (validityPeriod, initialIssueDate, version) => {
  if (version === 1) {
    // Always use the full format for v1
    if (validityPeriod && validityPeriod.includes('至')) {
      return validityPeriod;
    }
    // If only single date, construct range using initialIssueDate
    const startDate = formatDate(initialIssueDate);
    const endDate = formatDate(validityPeriod);
    return `${startDate}至${endDate}`;
  }
  // For v2, use the format as is
  return validityPeriod;
};

/**
 * Formats a date string to Chinese format
 * @param {string} dateString - The date string to format
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return `${date.getFullYear()}年${String(date.getMonth() + 1).padStart(2, '0')}月${String(date.getDate()).padStart(2, '0')}日`;
  } catch (e) {
    console.error('日期格式化失败:', e);
    return dateString;
  }
}; 